/**
 *
 * MobileBottomBar
 *
 */

import React, { memo, useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { compose } from "redux";
import classNames from "classnames";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { List, Drawer } from "antd";
import { TabBar } from "antd-mobile";

import history from "utils/history";

// assets
import CustomIcons from "components/CustomIcons";
import { MenuOutlined, MoreOutlined } from "@ant-design/icons";

// global user
import { withUser } from "engine/Contexts/User.context";
// hooks
import { useRefState } from "utils/hooks";
import { getObjId } from "utils/helpers/helper.general";
import _local from "utils/localStorageUtils";

function MobileBottomBar({
  selectedTab,
  // states
  user,
  // actions
  changeTab,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames("fixed-nav-mobile", className);
  const [t, i18n] = useTranslation();

  const historyStates = history.location?.state;

  const localUser = _local.getLocalUser();
  const activeChild = _local.getLocalChild();
  const activeSchool = _local.getLocalSchool();

  const location = useLocation();
  const nameMenuSplit = location.pathname.split("/");
  const dafaultPath = `/${nameMenuSplit[1]}`;

  const [
    notReadCommAmount,
    notReadCommAmountRef,
    setNotReadCommAmount,
  ] = useRefState(false);

  const [notifications, setNotifications] = useState([]);

  const [absAmount, absAmountRef, setAbsAmount] = useRefState(false);

  const [defaultActiveItem, setDefaultActiveItem] = useState(dafaultPath);
  const [drawerOpened, setDrawerOpened] = useState(false);

  const getChildNotif = useCallback(
    async (childID, reset = false) => {
      let notif = notifications || user?.childsNotifications;
      const hasNotif = notif?.find(el => getObjId(el) == getObjId(childID));
      if (!!hasNotif && !reset) {
        setNotReadCommAmount(hasNotif?.totalAmount);
        setAbsAmount(hasNotif?.absNeedActionsAmount);
      } else {
        const result = await user.getChildNotification(childID);

        setNotReadCommAmount(result?.totalAmount);
        setAbsAmount(result?.absNeedActionsAmount);
      }
    },
    [location.pathname, notifications, user?.childsNotifications],
  );

  const getSchoolNotif = useCallback(
    async (schoolKey, reset = false) => {
      let notif = notifications || user?.schoolsNotifications;
      const hasNotif = notif?.find(el => el?.key == schoolKey);
      if (!!hasNotif && !reset) {
        setNotReadCommAmount(hasNotif?.totalAmount);
        setAbsAmount(hasNotif?.absNeedActionsAmount);
      } else {
        const result = await user.getSchoolNotification(schoolKey);

        setNotReadCommAmount(result?.totalAmount);
        setAbsAmount(result?.absNeedActionsAmount);
      }
    },
    [location.pathname, notifications, user?.schoolsNotifications],
  );

  // for teacher
  useEffect(() => {
    if (localUser?.role == "teacher") {
      setNotifications(user?.schoolsNotifications);
      if (activeChild?.key) {
        getSchoolNotif(activeSchool?.key);
      } else {
        setNotReadCommAmount(false);
        setAbsAmount(false);
      }
    }
  }, [activeSchool?.key, user?.schoolsNotifications]);

  // for parent
  useEffect(() => {
    if (localUser?.role == "parent") {
      setNotifications(user?.childsNotifications);
      if (activeChild?._id) {
        getChildNotif(activeChild?._id);
      } else {
        setNotReadCommAmount(false);
        setAbsAmount(false);
      }
    }
  }, [activeChild?._id, user?.childsNotifications]);

  // Get and change path from location into bottom bar
  useEffect(() => {
    const linkSplit = location.pathname.split("/");
    const newPath = `/${linkSplit[1]}`;
    if (historyStates?.cameFrom == "single_communication") {
      if (localUser?.role == "teacher") {
        getSchoolNotif(activeSchool?.key, true);
      } else if (localUser?.role == "parent") {
        getChildNotif(activeChild?._id, true);
      }
    }
    if (newPath !== defaultActiveItem) {
      setDefaultActiveItem(newPath);
      if (localUser?.role == "teacher") {
        getSchoolNotif(activeSchool?.key, true);
      } else if (localUser?.role == "parent") {
        getChildNotif(activeChild?._id, true);
      }
    }
  }, [location.pathname]);

  // toggle drawers
  const openDrawer = useCallback(() => {
    setDrawerOpened(prev => !prev);
  }, []);

  const communicationItem = useMemo(() => {
    return (
      <TabBar.Item
        title={t("layouts.mobileNavigation.news")}
        key="/communications"
        icon={
          <>
            {selectedTab === "/communications" ||
            selectedTab === "/" ||
            selectedTab === "/home" ? (
              <CustomIcons type="letter-close-active" />
            ) : (
              <CustomIcons type="letter-close" />
            )}
          </>
        }
        badge={!!notReadCommAmountRef?.current && notReadCommAmountRef?.current}
        data-seed="logId"
      />
    );
  }, [
    notReadCommAmount,
    notReadCommAmountRef?.current,
    selectedTab,
    location.pathname,
    notifications,
    user?.childsNotifications,
    user?.schoolsNotifications,
    activeChild?._id,
    activeSchool?.key,
    i18n?.language
  ]);

  const absencesItem = useMemo(() => {
    return (
      <TabBar.Item
        title={t("layouts.mobileNavigation.absences")}
        key="/absences"
        icon={
          <>
            {selectedTab === "/absences" ? (
              <CustomIcons type="absence-active" />
            ) : (
              <CustomIcons type="absence" />
            )}
          </>
        }
        badge={!!absAmountRef?.current && absAmountRef?.current}
        data-seed="logId"
      />
    );
  }, [
    localUser?.role,
    absAmount,
    absAmountRef?.current,
    selectedTab,
    location.pathname,
    defaultActiveItem,
    notifications,
    user?.childsNotifications,
    user?.schoolsNotifications,
    activeChild?._id,
    activeSchool?.key,
    i18n?.language
  ]);

  // const schoolItem = useMemo(
  //   () =>
  //     localUser?.role == "parent" && (
  //       <TabBar.Item
  //         title="École"
  //         key="/ecole"
  //         icon={
  //           <>
  //             {selectedTab === "/ecole" ? (
  //               <CustomIcons className="school" type="school-active" />
  //             ) : (
  //               <CustomIcons className="school" type="school" />
  //             )}
  //           </>
  //         }
  //         // badge={1}
  //         data-seed="logId"
  //       />
  //     ),
  //   [localUser?.role, selectedTab, i18n?.language],
  // );

  const coursItem = useMemo(
    () => (
      <TabBar.Item
        title={t("layouts.mobileNavigation.agenda")}
        key="/сours"
        icon={
          <>
            {selectedTab === "/сours" ? (
              <CustomIcons type="table-active" />
            ) : (
              <CustomIcons type="table" />
            )}
          </>
        }
        // badge={1}
        data-seed="logId"
      />
    ),
    [selectedTab, i18n?.language],
  );

  const presencesItem = useMemo(
    () =>
      localUser?.role == "teacher" && (
        <TabBar.Item
          title={t("layouts.mobileNavigation.presences")}
          key="/attendance"
          icon={
            <>
              {selectedTab === "/attendance" ? (
                <CustomIcons type="absence-checked-active" />
              ) : (
                <CustomIcons type="absence-checked" />
              )}
            </>
          }
          // badge={1}
          data-seed="logId"
        />
      ),
    [localUser?.role, selectedTab, i18n?.language],
  );

  // const moreItems = useMemo(
  //   () => (
  //     <TabBar.Item
  //       title="Plus"
  //       key="/menu"
  //       icon={
  //         <>
  //           {selectedTab === "/menu" ? (
  //             <CustomIcons className="dots-horizontal" type="dots-vertical" />
  //           ) : (
  //             <CustomIcons className="dots-horizontal" type="dots-vertical" />
  //           )}
  //         </>
  //       }
  //     />
  //   ),
  //   [selectedTab, openDrawer],
  // );

  const sideContent = useMemo(() => {
    return (
      <List className="mobile-bottom-sidebar-menu">
        <List.Item
          className="mobile-bottom-sidebar-menu__item"
          key={"communications"}
        >
          <Link
            className="menu-item-link"
            to={`/communications`}
            onClick={() => setDrawerOpened(false)}
          >
            <CustomIcons className="menu-item-icon" type="letter-close" /> {t("labels.news")}
          </Link>
        </List.Item>

        <List.Item
          className="mobile-bottom-sidebar-menu__item"
          key={"absences"}
        >
          <Link
            className="menu-item-link"
            to={`/absences`}
            onClick={() => setDrawerOpened(false)}
          >
            <CustomIcons className="menu-item-icon" type="absence" />
            {t("labels.absences")}
          </Link>
        </List.Item>

        <List.Item className="mobile-bottom-sidebar-menu__item" key={"сours"}>
          <Link
            className="menu-item-link"
            to={`/сours`}
            onClick={() => setDrawerOpened(false)}
          >
            <CustomIcons className="menu-item-icon" type="table" />
            {t("labels.agenda")}
          </Link>
        </List.Item>

        {localUser?.role == "teacher" && (
          <List.Item
            className="mobile-bottom-sidebar-menu__item"
            key={"attendance"}
          >
            <Link
              className="menu-item-link"
              to={`/attendance`}
              onClick={() => setDrawerOpened(false)}
            >
              <CustomIcons
                className="menu-item-icon school absence-checked-icon"
                type="absence-checked"
              />{" "}
              {t("labels.presences")}
            </Link>
          </List.Item>
        )}

        <List.Item className="mobile-bottom-sidebar-menu__item" key={"ecole"}>
          <Link
            className="menu-item-link"
            to={`/ecole`}
            onClick={() => setDrawerOpened(false)}
          >
            <CustomIcons className="menu-item-icon school" type="school" />{" "}
            {t("layouts.navigation.school")}
          </Link>
        </List.Item>

        {localUser?.role == "parent" && (
          <List.Item
            className="mobile-bottom-sidebar-menu__item"
            key={"linkstudent"}
          >
            <Link
              className="menu-item-link"
              to={`/linkstudent`}
              onClick={() => setDrawerOpened(false)}
            >
              <CustomIcons className="menu-item-icon" type="qrcode" />
              {t("layouts.navigation.qr")}
            </Link>
          </List.Item>
        )}
      </List>
    );
  }, [localUser?.role, selectedTab, i18n?.language]);

  return (
    <div className={childClassNames} {...rest}>
      <TabBar
        className="mobile-tab-bar"
        tabBarPosition="bottom"
        activeKey={defaultActiveItem}
        onChange={value => {
          // open bottom side menu
          if (value.indexOf("menu") != -1) {
            openDrawer();
          } else {
            changeTab(value);
          }
        }}
      >
        {communicationItem}
        {absencesItem}
        {coursItem}
        {presencesItem}
        {/* {schoolItem} */}
        {/* {moreItems} */}
      </TabBar>
      <Drawer
        key="tcf-bottom-mobile-drawer"
        className={`tcf-bottom-mobile-drawer ${localUser?.role}`}
        style={{
          minHeight: document.documentElement.clientHeight,
        }}
        contentStyle={{
          color: "#A6A6A6",
          textAlign: "center",
          paddingTop: 42,
        }}
        sidebar={sideContent}
        open={drawerOpened}
        onClose={openDrawer}
        position={"right"}
        touch={true}
        maskClosable={true}
        docked={false}
        closable={false}
      >
        {sideContent}
      </Drawer>
    </div>
  );
}

MobileBottomBar.propTypes = {
  selectedTab: PropTypes.string,
  changeTab: PropTypes.func.isRequired,
};

export default compose(
  memo,
  withUser,
)(MobileBottomBar);
